<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { devices } from 'src/stores/lists/devices';
	import {
		getDeviceStatus,
		readDeviceSettings,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import SettingGuide from '../../SettingGuide.svelte';
	import { Alert, ButtonV2, Container, Text, TokenV2 } from '@Shelly/shelly-design-system';
	import { devices_config } from '@devices/index';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		get_gen_2_component_channel_structure,
		get_mode,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { deviceHasSettingTitle } from 'src/helpers/settings/device_has_setting_title';
	import { isEmeterEm1 } from 'src/helpers/settings/emeter_settings_helpers';
	import { capFirstChar, genCharArray } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';

	export let device: Device;

	const settings = readDeviceSettings(device.id);
	const isEm1 = isEmeterEm1(device);
	const showPhaseCalibration = deviceHasSettingTitle(device, 'phase_calibration');
	const showChannelCalibration = deviceHasSettingTitle(device, 'channel_from_channel_calibration');
	const mode = get_mode(device, readDeviceStatus(device.id), settings);
	const phases = genCharArray(devices_config[device.type]?.modes?.[mode]?.phases);
	const toPhases = [...phases, 'n'];
	const otherChannelId = device.id.indexOf('_') > -1 ? real_id(device.id) : `${device.id}_1`;
	const otherChannelIndex = device.id.indexOf('_') > -1 ? 0 : 1;

	let phaseFrom = phases[0] || 'a';
	let phaseTo = phases[1] || 'n';
	let phaseForReset = '';
	let calibrateLoading = false;
	let resetLoading = false;
	let channelLoading = false;
	let revertLoading = false;

	$: statusStore = getDeviceStatus(device.id);
	$: userCalibratedPhases =
		$statusStore?.[get_gen_2_component_channel_structure(device)]?.user_calibrated_phase || [];
	$: hasCalibratedPhase = userCalibratedPhases.length > 0;
	$: if (hasCalibratedPhase && !userCalibratedPhases.includes(phaseForReset)) {
		phaseForReset = userCalibratedPhases[0];
	}
	$: otherChannelName = $devices[otherChannelId]?.name || '';

	$: if (phaseTo === phaseFrom) {
		phaseTo = toPhases.find((phase) => phase !== phaseFrom) || 'n';
	}

	const phaseLabel = (phase: string): string =>
		$format(`label_phase_${phase}`, { default: `Phase ${phase}` });

	const calibratePhases = async () => {
		calibrateLoading = true;
		const res: SettingRequest = await gen2Request(device, 'EM.PhaseToPhaseCalib', {
			id: 0,
			from: phaseFrom,
			to: phaseTo,
		});
		onSettingsResponse(res, device);
		calibrateLoading = false;
	};

	const resetPhaseCalibration = async () => {
		if (!hasCalibratedPhase) return;
		resetLoading = true;
		const res: SettingRequest = await gen2Request(device, 'EM.PhaseToPhaseCalibReset', {
			id: 0,
			phase: phaseForReset,
		});
		onSettingsResponse(res, device);
		resetLoading = false;
	};

	const calibrateFromChannel = async () => {
		channelLoading = true;
		const res: SettingRequest = await gen2Request(device, 'EM1.CalibrateFrom', {
			id: device.channel,
			other_id: otherChannelIndex,
		});
		onSettingsResponse(res, device, { danger: res?.errors?.message });
		channelLoading = false;
	};

	const revertFactoryCalibration = async () => {
		revertLoading = true;
		const res: SettingRequest = await gen2Request(device, 'EM1.RevertToFactoryCalibration', {
			id: device.channel,
		});
		onSettingsResponse(res, device, {
			success: $format('toast_revert_factory_calibration_success', {
				default: 'Factory calibration restored.',
			}),
		});
		revertLoading = false;
	};
</script>

<div class="emeter-calibration">
	<SettingGuide
		text={$format('settings_guide_emeter_calibration', {
			default:
				'Copy CT calibration from a reference phase or channel. Phases must be on the same voltage, with at least 500 W of load.',
		})}
	/>

	{#if showPhaseCalibration && !isEm1}
		<Alert
			type="warning"
			heading={$format('heading_phase_to_phase', {
				default: 'Phase to phase calibration',
			})}
			message={$format('calibrate_phases_description', {
				default:
					'In order to be able to calibrate correctly the phase voltages need to be equal i.e. if we are calibrating a current transformer from Channel 0 to a current transformer on Channel 1, the voltage of Channel 0 and Channel 1 needs to be the same. Note: A minimum load is required for successful phase to phase calibration.',
			})}
		/>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_phase_calibration', {
					default: 'Calibrate phases',
				})}
			</Text>
			<Text styleType="body-small" class="mb-xs text-color-muted">
				{$format('settings_heading_emeter_calibrate_from', {
					default: 'From phase',
				})}
			</Text>
			<div class="d-flex flex-wrap mb-s">
				{#each phases as phase (phase)}
					<TokenV2
						id={`token-emeter-calib-from-${phase}`}
						selected={phaseFrom === phase}
						disabled={calibrateLoading}
						size="m"
						title={phaseLabel(phase)}
						class="mr-s mb-s"
						aria-label={phaseLabel(phase)}
						on:click={() => {
							phaseFrom = phase;
						}}
					/>
				{/each}
			</div>
			<Text styleType="body-small" class="mb-xs text-color-muted">
				{$format('settings_heading_emeter_calibrate_to', {
					default: 'To phase',
				})}
			</Text>
			<div class="d-flex flex-wrap mb-s">
				{#each toPhases as phase (phase)}
					<TokenV2
						id={`token-emeter-calib-to-${phase}`}
						selected={phaseTo === phase}
						disabled={calibrateLoading || phaseFrom === phase}
						size="m"
						title={phaseLabel(phase)}
						class="mr-s mb-s"
						aria-label={phaseLabel(phase)}
						on:click={() => {
							phaseTo = phase;
						}}
					/>
				{/each}
			</div>
			<ButtonV2
				size="m"
				width="full"
				loading={calibrateLoading}
				disabled={calibrateLoading}
				aria-label={$format('button_calibrate', { default: 'Calibrate' })}
				on:click={calibratePhases}
			>
				{$format('button_calibrate', { default: 'Calibrate' })}
			</ButtonV2>
		</Container>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_reset_calibration', {
					default: 'Reset calibration',
				})}
			</Text>
			{#if hasCalibratedPhase}
				<Text styleType="body-small" class="mb-s text-color-muted">
					{$format('phases_differece_description', {
						default: 'Phases below differs from factory calibration.',
					})}
				</Text>
				<div class="d-flex flex-wrap mb-s">
					{#each userCalibratedPhases as phase (phase)}
						<TokenV2
							id={`token-emeter-calib-reset-${phase}`}
							selected={phaseForReset === phase}
							disabled={resetLoading}
							size="m"
							title={phaseLabel(phase)}
							class="mr-s mb-s"
							aria-label={phaseLabel(phase)}
							on:click={() => {
								phaseForReset = phase;
							}}
						/>
					{/each}
				</div>
			{:else}
				<Alert
					class="mb-s"
					type="info"
					heading={$format('label_info', { default: 'Information' })}
					message={$format('phases_no_difference_description', {
						default: 'All phases are with their factory calibrations.',
					})}
				/>
			{/if}
			<ButtonV2
				size="m"
				width="full"
				loading={resetLoading}
				disabled={resetLoading || !hasCalibratedPhase}
				aria-label={capFirstChar($format('button_reset', { default: 'reset' }))}
				on:click={resetPhaseCalibration}
			>
				{capFirstChar($format('button_reset', { default: 'reset' }))}
			</ButtonV2>
		</Container>
	{/if}

	{#if showChannelCalibration}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_channel_calibration', {
					default: 'Channel calibration',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_guide_channel_calibration', {
					default:
						'Use "{otherChannelName}" as the reference to calibrate this channel. Connect both on the same line, with some load running.',
					values: { otherChannelName },
				})}
			</Text>
			<ButtonV2
				size="m"
				width="full"
				loading={channelLoading}
				disabled={channelLoading}
				aria-label={$format('button_calibrate', { default: 'Calibrate' })}
				on:click={calibrateFromChannel}
			>
				{$format('button_calibrate', { default: 'Calibrate' })}
			</ButtonV2>
		</Container>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_factory_calibration', {
					default: 'Factory calibration',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_guide_emeter_factory_calibration', {
					default:
						'Revert this channel to the factory CT calibration. Firmware reports an error if this is not available.',
				})}
			</Text>
			<ButtonV2
				size="m"
				width="full"
				loading={revertLoading}
				disabled={revertLoading}
				aria-label={$format('button_revert_factory_calibration', {
					default: 'Revert to factory calibration',
				})}
				on:click={revertFactoryCalibration}
			>
				{$format('button_revert_factory_calibration', {
					default: 'Revert to factory calibration',
				})}
			</ButtonV2>
		</Container>
	{/if}
</div>

<style>
	.emeter-calibration {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
